import { useEffect, useState } from "react";
import { format, addDays, startOfWeek } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { Database } from "@/integrations/supabase/types";

type Slot = Database["public"]["Tables"]["availability_slots"]["Row"] & { specific_date?: string | null };
type Booking = Database["public"]["Tables"]["bookings"]["Row"];

interface PlanningGridProps {
  proId: string;
  weekStart: Date;
  onSlotClick?: (slot: Slot | null, date: Date, hour: number) => void;
  isProView?: boolean;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i); // 0h-23h
const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const hourFrom = (t: string) => parseInt(t.split(":")[0]);

/** Does the slot cover this (dayIndex, hour, date)? Handles wrap-around midnight. */
const slotCovers = (s: Slot, dayIndex: number, hour: number, dateStr: string, prevDateStr: string): boolean => {
  const sH = hourFrom(s.start_time);
  const eH = hourFrom(s.end_time);
  const wraps = eH <= sH;
  // Specific date slot: applies ONLY on its own date (and next day if wraps)
  if (s.specific_date) {
    if (!wraps) {
      return s.specific_date === dateStr && hour >= sH && hour < eH;
    }
    return (s.specific_date === dateStr && hour >= sH) ||
           (s.specific_date === prevDateStr && hour < eH);
  }
  // Recurring slot
  if (!wraps) {
    return s.day_of_week === dayIndex && hour >= sH && hour < eH;
  }
  const prevDay = (dayIndex + 6) % 7;
  return (s.day_of_week === dayIndex && hour >= sH) ||
         (s.day_of_week === prevDay && hour < eH);
};

const bookingCovers = (b: Booking, dateStr: string, prevDateStr: string, hour: number): boolean => {
  const sH = hourFrom(b.start_time);
  const eH = hourFrom(b.end_time);
  const wraps = eH <= sH;
  if (!wraps) return b.booking_date === dateStr && hour >= sH && hour < eH;
  return (b.booking_date === dateStr && hour >= sH) ||
         (b.booking_date === prevDateStr && hour < eH);
};

const PlanningGrid = ({ proId, weekStart, onSlotClick, isProView = false }: PlanningGridProps) => {
  const [slots, setSlots] = useState<Slot[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);

  const monday = startOfWeek(weekStart, { weekStartsOn: 1 });
  const weekEndStr = format(addDays(monday, 6), "yyyy-MM-dd");
  const mondayStr = format(monday, "yyyy-MM-dd");

  useEffect(() => {
    const fetchData = async () => {
      const [{ data: slotsData }, { data: bookingsData }] = await Promise.all([
        supabase.from("availability_slots").select("*").eq("pro_id", proId),
        supabase
          .from("bookings")
          .select("*")
          .eq("pro_id", proId)
          .in("status", ["pending", "accepted"])
          .gte("booking_date", mondayStr)
          .lte("booking_date", weekEndStr),
      ]);
      setSlots((slotsData ?? []) as Slot[]);
      setBookings(bookingsData ?? []);
    };
    fetchData();

    const channel = supabase
      .channel(`planning-${proId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "bookings", filter: `pro_id=eq.${proId}` }, fetchData)
      .on("postgres_changes", { event: "*", schema: "public", table: "availability_slots", filter: `pro_id=eq.${proId}` }, fetchData)
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [proId, mondayStr, weekEndStr]);

  type CellState = "empty" | "available" | "blocked" | "pending" | "accepted";

  const getCellState = (dayIndex: number, hour: number): { state: CellState; slot?: Slot; booking?: Booking } => {
    const dateStr = format(addDays(monday, dayIndex), "yyyy-MM-dd");
    const prevDateStr = format(addDays(monday, dayIndex - 1), "yyyy-MM-dd");

    // Specific-date slots take priority
    const matches = slots.filter((s) => slotCovers(s, dayIndex, hour, dateStr, prevDateStr));
    const slot = matches.find((s) => !!s.specific_date) ?? matches[0];
    if (!slot) return { state: "empty" };
    if (slot.is_blocked) return { state: "blocked", slot };
    const booking = bookings.find((b) => bookingCovers(b, dateStr, prevDateStr, hour));
    if (booking?.status === "pending") return { state: "pending", slot, booking };
    if (booking?.status === "accepted") return { state: "accepted", slot, booking };
    return { state: "available", slot };
  };

  const cellStyles: Record<CellState, string> = {
    empty: "bg-muted/20 border-transparent",
    available: "bg-primary/10 border-primary/20 hover:bg-primary/20",
    blocked: "bg-muted/60 border-muted-foreground/20 opacity-70",
    pending: "bg-amber-500/15 border-amber-500/30 animate-pulse",
    accepted: "bg-secondary/30 border-secondary/40",
  };

  const cellLabels: Record<CellState, string> = {
    empty: "",
    available: "Dispo",
    blocked: "Bloqué",
    pending: "En attente",
    accepted: "Confirmé",
  };

  const cellDots: Record<CellState, React.ReactNode> = {
    empty: null,
    available: <span className="w-1.5 h-1.5 rounded-full bg-primary inline-block" />,
    blocked: <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 inline-block" />,
    pending: <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />,
    accepted: <span className="w-1.5 h-1.5 rounded-full bg-secondary inline-block" />,
  };

  const hasAnySlots = slots.length > 0;

  return (
    <div className="w-full max-w-full overflow-x-auto overscroll-x-contain pb-2" role="region" aria-label="Planning hebdomadaire">
      {!hasAnySlots && !isProView && (
        <div className="text-center py-8 text-muted-foreground bg-muted/30 rounded-xl mb-4">
          <p className="font-medium">Aucun créneau disponible</p>
          <p className="text-sm mt-1">Ce professionnel n'a pas encore défini ses disponibilités.</p>
        </div>
      )}
      <div className="w-max min-w-[520px] sm:min-w-[600px]">
        <div className="grid grid-cols-[48px_repeat(7,64px)] sm:grid-cols-[60px_repeat(7,minmax(70px,1fr))] gap-1 mb-1 sticky top-0 bg-background z-10">
          <div />
          {DAYS.map((day, i) => (
            <div key={day} className="text-center text-xs font-semibold text-foreground py-2">
              <div>{day}</div>
              <div className="text-muted-foreground font-normal">{format(addDays(monday, i), "dd/MM")}</div>
            </div>
          ))}
        </div>

        <div className="max-h-[70vh] overflow-y-auto pr-1">
          {HOURS.map((hour) => (
            <div key={hour} className="grid grid-cols-[48px_repeat(7,64px)] sm:grid-cols-[60px_repeat(7,minmax(70px,1fr))] gap-1 mb-1">
              <div className="text-[10px] sm:text-xs text-muted-foreground flex items-center justify-end pr-2 font-medium">
                {String(hour).padStart(2, "0")}:00
              </div>
              {DAYS.map((_, dayIndex) => {
                const { state, slot } = getCellState(dayIndex, hour);
                const canParentClick = !isProView && state === "available";
                const canProClick = isProView; // pro can click any cell
                const clickable = canParentClick || canProClick;

                return (
                  <Tooltip key={dayIndex}>
                    <TooltipTrigger asChild>
                      <button
                        disabled={!clickable}
                        onClick={() => {
                          if (clickable && onSlotClick) {
                            onSlotClick(slot ?? null, addDays(monday, dayIndex), hour);
                          }
                        }}
                        className={cn(
                          "h-8 sm:h-9 rounded-md border text-[10px] sm:text-[11px] flex items-center justify-center gap-1 transition-colors",
                          cellStyles[state],
                          clickable && "cursor-pointer hover:shadow-sm",
                          !clickable && state === "empty" && "cursor-default",
                        )}
                      >
                        {cellDots[state]}
                        <span className="hidden sm:inline">{cellLabels[state]}</span>
                      </button>
                    </TooltipTrigger>
                    {(state !== "empty" || isProView) && (
                      <TooltipContent>
                        <p>{cellLabels[state] || "Vide — cliquer pour créer"}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                );
              })}
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-4 mt-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-primary/10 border border-primary/20" /> Disponible</div>
          <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-500/15 border border-amber-500/30 animate-pulse" /> En attente</div>
          <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-secondary/30 border border-secondary/40" /> Confirmé</div>
          <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-muted/60 border" /> Bloqué</div>
        </div>
      </div>
    </div>
  );
};

export default PlanningGrid;
